import { mapActions } from 'vuex'
import { formatVersionHistoryTimeAgo, formatVersionHistoryTimestamp } from '@/utils/versionHistoryTime'

export const authorStorageKey = (sharingKey) => `cg_comment_author_${sharingKey || 'workspace'}`

export const readStoredAuthor = (sharingKey) => {
  try {
    const stored = window.localStorage.getItem(authorStorageKey(sharingKey))
    const parsed = stored ? JSON.parse(stored) : null
    return parsed?.name ? parsed : null
  } catch (e) {
    return null
  }
}

export const writeStoredAuthor = (sharingKey, author) => {
  try {
    window.localStorage.setItem(authorStorageKey(sharingKey), JSON.stringify(author))
  } catch (e) {
    console.error('Could not remember the comment author:', e)
  }
}

export default {
  name: 'ArticleComments',
  props: {
    articleId: {
      type: [Number, String],
      default: null
    },
    contentplanId: {
      type: [Number, String],
      default: null
    },
    sharingKey: {
      type: String,
      default: null
    },
    threads: {
      type: Array,
      default: () => []
    },
    activeThreadId: {
      type: [Number, String],
      default: null
    },
    pendingAnchor: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      filter: 'open',
      newComment: '',
      replyDrafts: {},
      replyingTo: null,
      isSaving: false,
      authorDialog: false,
      authorForm: { name: '', email: '' },
      author: readStoredAuthor(this.sharingKey)
    }
  },
  computed: {
    filters () {
      return [
        { text: 'Open', value: 'open', count: this.openThreads.length },
        { text: 'Resolved', value: 'resolved', count: this.resolvedThreads.length },
        { text: 'Changed text', value: 'orphaned', count: this.orphanedThreads.length }
      ]
    },
    openThreads () {
      return this.threads.filter(thread => thread.status === 'open' && !thread.orphaned)
    },
    resolvedThreads () {
      return this.threads.filter(thread => thread.status === 'resolved')
    },
    orphanedThreads () {
      return this.threads.filter(thread => thread.status === 'open' && thread.orphaned)
    },
    visibleThreads () {
      if (this.filter === 'resolved') return this.resolvedThreads
      if (this.filter === 'orphaned') return this.orphanedThreads
      return this.openThreads
    },
    // Anonymous share-link visitors can add feedback but not change existing threads.
    canModerate () {
      return !this.sharingKey
    }
  },
  watch: {
    sharingKey (value) {
      this.author = readStoredAuthor(value)
    },
    pendingAnchor (anchor) {
      if (anchor) this.filter = 'open'
    }
  },
  methods: {
    ...mapActions([
      'articleCommentsPost',
      'articleCommentsPut'
    ]),
    timeAgo (timestamp) {
      return formatVersionHistoryTimeAgo(timestamp)
    },
    timestampLabel (timestamp) {
      return formatVersionHistoryTimestamp(timestamp)
    },
    truncateQuote (quote, maxLength = 140) {
      if (!quote) return ''
      return quote.length <= maxLength ? quote : `${quote.slice(0, maxLength - 3)}...`
    },
    needsAuthor () {
      return Boolean(this.sharingKey) && !this.author?.name
    },
    openAuthorDialog () {
      this.authorForm = { name: this.author?.name || '', email: this.author?.email || '' }
      this.authorDialog = true
    },
    saveAuthor () {
      const name = this.authorForm.name?.trim()
      if (!name) return
      this.author = { name, email: this.authorForm.email?.trim() || null }
      writeStoredAuthor(this.sharingKey, this.author)
      this.authorDialog = false
    },
    cancelNewComment () {
      this.newComment = ''
      this.$emit('cancel-anchor')
    },
    async submitNewComment () {
      const body = this.newComment?.trim()
      if (!body || !this.pendingAnchor) return
      if (this.needsAuthor()) return this.openAuthorDialog()

      await this.saveComment({
        quote: this.pendingAnchor.quote,
        prefix: this.pendingAnchor.prefix,
        suffix: this.pendingAnchor.suffix,
        startOffset: this.pendingAnchor.startOffset,
        body
      })

      this.newComment = ''
      this.$emit('cancel-anchor')
    },
    async submitReply (thread) {
      const body = this.replyDrafts[thread.id]?.trim()
      if (!body) return
      if (this.needsAuthor()) return this.openAuthorDialog()

      await this.saveComment({ parentId: thread.id, body })

      this.$set(this.replyDrafts, thread.id, '')
      this.replyingTo = null
    },
    async saveComment (payload) {
      this.isSaving = true
      try {
        await this.articleCommentsPost({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          authorName: this.author?.name,
          authorEmail: this.author?.email,
          ...payload
        })
        this.$emit('refresh')
      } catch (e) {
        console.error('Failed to save comment:', e)
      }
      this.isSaving = false
    },
    async updateComment (thread, action) {
      this.isSaving = true
      try {
        await this.articleCommentsPut({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          commentId: thread.id,
          sharingKey: this.sharingKey,
          action
        })
        this.$emit('refresh')
      } catch (e) {
        console.error(`Failed to ${action} comment:`, e)
      }
      this.isSaving = false
    }
  }
}
